import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import PropTypes from "prop-types";
import { GoogleMap, LoadScript, Marker, DistanceMatrixService } from "@react-google-maps/api";

import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import SectionHeader from "components/patterns/SectionHeader";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";
import MDAlertCloseIcon from "components/MDAlert/MDAlertCloseIcon";
import HelpTooltip from "components/HelpTooltip";

import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import DataTable from "components/patterns/DataTable";
import ReportsBarChart from "components/patterns/charts/ReportsBarChart";
import ReportsBarNoLabelChart from "./components/ReportsBarNoLabelChart";
import MultiAxisChart from "components/patterns/charts/MultiAxisChart";
import FormField from "components/FormField";

import { useApiClient } from "common/ApiClientProvider";

/*
const center = {
  lat: 1.29,
  lng: 103.85,
};
*/
function GeoMap({
  center,
  siteMarkers,
  clickedMarkers,
  onClick,
  lat,
  lng,
  siteLat,
  siteLng,
  setDistance,
}) {
  return (
    <LoadScript googleMapsApiKey="AIzaSyCMRlsJIkTmawGuiXktiAnHHWCm3Axt7r8">
      <GoogleMap
        zoom={10}
        center={center}
        options={{ scrollwheel: false }}
        onClick={onClick}
        mapContainerStyle={{ height: `560px`, borderRadius: "6px", overflow: "hidden" }}
      >
        {siteMarkers}
        {clickedMarkers}
        <DistanceMatrixService
          options={{
            destinations: [{ lat: lat, lng: lng }],
            origins: [{ lat: siteLat, lng: siteLng }],
            travelMode: "DRIVING",
          }}
          callback={(response) => {
            const distance = (response.rows[0].elements[0].distance?.value ?? 0) / 1000;
            setDistance(distance);
          }}
        />
      </GoogleMap>
    </LoadScript>
  );
}

GeoMap.propTypes = {
  center: PropTypes.object.isRequired,
  siteMarkers: PropTypes.arrayOf(PropTypes.object).isRequired,
  clickedMarkers: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClick: PropTypes.func.isRequired,
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  siteLat: PropTypes.number.isRequired,
  siteLng: PropTypes.number.isRequired,
  setDistance: PropTypes.func.isRequired,
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Stack
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      mt={3}
      justifyContent="center"
      {...other}
    >
      {value === index && children}
    </Stack>
  );
}

TabPanel.defaultProps = {
  children: "",
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function getSteps(t) {
  return [t("steps.howToUse"), t("steps.gridConnected"), t("steps.offGrid")];
}

function annuity(numPayments, interestRate, systemPrice) {
  const modifiedInterestRate = 1.0 + 0.01 * interestRate;
  let sum = 1;
  let fac = 1.0;
  for (let i = 0; i < numPayments - 1; i += 1) {
    fac *= modifiedInterestRate;
    sum += fac;
  }
  return (systemPrice * fac * modifiedInterestRate) / sum;
}

function getStepContent(stepIndex, currentTranslation) {
  switch (stepIndex) {
    case 0:
      return (
        <MDBox mx={5}>
          <MDTypography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {currentTranslation.tutorial1}
          </MDTypography>
        </MDBox>
      );
    case 1:
      return (
        <MDBox mx={5}>
          <MDTypography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {currentTranslation.tutorial2}
          </MDTypography>
        </MDBox>
      );
    case 2:
      return (
        <MDBox mx={5}>
          <MDTypography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {currentTranslation.tutorial3}
          </MDTypography>
        </MDBox>
      );
    default:
      return null;
  }
}

function ForecastAutocompleteField({ children, helpText, mt }) {
  return (
    <MDBox mb={3} mt={mt} display="flex" alignItems="flex-start" gap={1}>
      <MDBox flex={1} minWidth={0}>
        {children}
      </MDBox>
      <HelpTooltip title={helpText} sx={{ mt: 0.25, flex: "0 0 auto" }} />
    </MDBox>
  );
}

ForecastAutocompleteField.defaultProps = {
  mt: 0,
};

ForecastAutocompleteField.propTypes = {
  children: PropTypes.node.isRequired,
  helpText: PropTypes.string.isRequired,
  mt: PropTypes.number,
};

function PowerGenerationForecast() {
  const { t, i18n } = useTranslation(["forecast", "common", "pages"]);
  const { t: tHelp } = useTranslation("help");
  const location = useLocation();
  const apiClient = useApiClient();
  const [activeStep, setActiveStep] = useState(0);
  const steps = getSteps(t);
  const handleBack = () => setActiveStep(activeStep - 1);
  const handleForward = () => setActiveStep(activeStep + 1);
  const isLastStep = activeStep === steps.length - 1;

  const currentTranslation = new Proxy({}, { get: (_, key) => t(key) });
  const localizedMonths = t("months", { returnObjects: true });

  const [siteMarkers, setSiteMarkers] = useState([]);
  const [clickedMarkers, setClickedMarkers] = useState([]);
  const [pvprice, setPvprice] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [databaseDisabled, setDatabaseDisabled] = useState(true);
  const [systemCostDisabled, setSystemCostDisabled] = useState(true);
  const [interestDisabled, setInterestDisabled] = useState(true);
  const [serviceLifeDisabled, setServiceLifeDisabled] = useState(true);
  const [databaseOptions, setDatabaseOptions] = useState([]);
  const [database, setDatabase] = useState(null);
  const [inputDatabase, setInputDatabase] = useState("");
  const [peakPVPower, setPeakPVPower] = useState(1);
  const [systemLoss, setSystemLoss] = useState(14);
  const [slope, setSlope] = useState(35);
  const [azimuth, setAzimuth] = useState(0);
  const [impactAndCumulativePowerGraphData, setImpactAndCumulativePowerGraphData] = useState({});
  const [impactTableData, setImpactTableData] = useState({
    columns: [
      { Header: t("environmentalUi.years", { ns: "pages" }), accessor: "years" },
      {
        Header: t("environmentalUi.cumulativePower", { ns: "pages" }),
        accessor: "cumulativePower",
      },
      {
        Header: t("environmentalUi.impactPerUnit", { ns: "pages" }),
        accessor: "environmentalImpact",
      },
      {
        Header: t("environmentalUi.transportImpact", { ns: "pages" }),
        accessor: "transportImpact",
      },
      {
        Header: t("environmentalUi.totalImpact", { ns: "pages" }),
        accessor: "totalEnvironmentalImpact",
      },
    ],
    rows: [],
  });
  const [monthlyGraphData, setMonthlyGraphData] = useState({
    labels: localizedMonths,
    units: { x: t("month"), y: "" },
    datasets: { label: "", data: [] },
  });
  const [percentileGraphData, setPercentileGraphData] = useState({
    labels: [],
    units: { x: t("battery_charge_state"), y: "%" },
    datasets: { label: "", data: [] },
  });
  const [slopeAngle, setSlopeAngle] = useState(0);
  const [azimuthAngle, setAzimuthAngle] = useState(0);
  const [yearlyPVEnergyProduction, setYearlyPVEnergyProduction] = useState(0);
  const [yearlyInplaneIrradiation, setYearlyInplaneIrradiation] = useState(0);
  const [yearToYearVariability, setYearToYearVariability] = useState(0);
  const [angleOfIncidence, setAngleOfIncidence] = useState(0);
  const [spectralEffects, setSpectralEffects] = useState(0);
  const [temperatureAndLowIrradiance, setTemperatureAndLowIrradiance] = useState(0);
  const [totalLoss, setTotalLoss] = useState(0);
  const [systemCost, setSystemCost] = useState("");
  const [interest, setInterest] = useState("");
  const [serviceLife, setServiceLife] = useState("");
  const [pvElectricityCost, setPVElectricityCost] = useState("");

  const [offGridPeakPVPower, setOffGridPeakPVPower] = useState(50);
  const [offGridBatteryCapacity, setOffGridBatteryCapacity] = useState(600);
  const [offGridDischargeCutoffLimit, setOffGridDischargeCutoffLimit] = useState(40);
  const [offGridConsumptionPerDay, setOffGridConsumptionPerDay] = useState(300);
  const [offGridSlope, setOffGridSlop] = useState(35);
  const [offGridAzimuth, setOffGridAzimuth] = useState(0);

  const [offGridFullBatteryDays, setOffGridFullBatteryDays] = useState(0);
  const [offGridEmptyBatteryDays, setOffGridEmptyBatteryDays] = useState(0);
  const [offGridAverageEnergyNotCaptured, setOffGirdAverageEnergyNotCaptured] = useState(0);
  const [offGridAverageEnergyMissing, setOffGridAverageEnergyMissing] = useState(0);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");

  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  const [siteLat, setSiteLat] = useState(0);
  const [siteLng, setSiteLng] = useState(0);
  const [distance, setDistance] = useState(0);
  const [center, setCenter] = useState({
    lat: 1.29,
    lng: 103.85,
  });

  const [pvTechnologyOptions, setPVTechnologyOptions] = useState([
    {
      label: t("pv_technology_dropdown_crystalline_silicon"),
      code: "crystSi",
    },
    {
      label: t("pv_technology_dropdown_cis"),
      code: "CIS",
    },
    {
      label: t("pv_technology_dropdown_cdte"),
      code: "CdTe",
    },
    {
      label: currentTranslation.pv_technology_dropdown_unknown,
      code: "Unknown",
    },
  ]);
  const [pvTechnology, setPVTechnology] = useState(pvTechnologyOptions[0]);
  const [inputPVTechnology, setInputPVTechnology] = useState("");
  const [mountingPositionOptions, setMountingPositionOptions] = useState([
    {
      label: currentTranslation.mounting_position_dropdown_free_standing,
      code: "free",
    },
    {
      label: currentTranslation.mounting_position_dropdown_roof_added_building_integrated,
      code: "building",
    },
  ]);
  const [mountingPosition, setMountingPosition] = useState(mountingPositionOptions[0]);
  const [intputMountingPosition, setInputMountingPosition] = useState("");
  const [optimizeOptions, setOptimizeOptions] = useState([
    {
      label: currentTranslation.optimize_dropdown_none,
      code: "n",
    },
    {
      label: currentTranslation.optimize_dropdown_slope,
      code: "s",
    },
    {
      label: currentTranslation.optimize_dropdown_slope_and_azimuth,
      code: "sa",
    },
  ]);
  const [optimize, setOptimize] = useState(optimizeOptions[0]);
  const [inputOptimize, setInputOptimize] = useState("");
  const [impactResponse, setImpactResponse] = useState({});
  const navigationSite = location.state?.prop;
  const [loadingOpen, setLoadingOpen] = useState(Boolean(navigationSite));
  const handleLoadingClose = () => setLoadingOpen(false);
  const handleLoadingOpen = () => setLoadingOpen(true);
  const [isMapClicked, setIsMapClicked] = useState(false);
  const [showMapNotClickedAlert, setShowMapNotClickedAlert] = useState(false);

  //const databases = ["PVGIS-SARAH3", "PVGIS-SARAH2", "PVGIS-NSRDB", "PVGIS-ERA5", "PVGIS-SARAH"];
  const databases = ["PVGIS-SARAH3", "PVGIS-ERA5"];

  const textfield = {
    "& .MuiInputBase-input.MuiAutocomplete-input": {
      color: "#ffffff",
    },
  };

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  function updateImpactDisplays(response, currentDistance) {
    const serviceLifeYears = response.service_life_years ?? 0;
    const labels = ["10", "15", "30", serviceLifeYears.toString()];
    const transportImpact = response.transport_100km?.map((x) => x * (currentDistance / 100)) ?? [];

    setImpactAndCumulativePowerGraphData({
      labels,
      units: { x: t("years"), y: "mPt/kWh", y2: "kWh" },
      datasets1: [
        {
          label: currentTranslation.environmental_impact_per_functional_unit,
          data: response.environmental_impacts,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        },
        {
          label: currentTranslation.junction_box_repair,
          data: response.junctionbox_repair,
          backgroundColor: "rgba(0, 255, 255, 0.8)",
        },
        {
          label: currentTranslation.transport,
          data: transportImpact,
          backgroundColor: "rgba(255, 0, 255, 0.8)",
        },
      ],
      datasets2: [
        {
          label: currentTranslation.cumulative_power,
          data: response.cumulative_power,
          backgroundColor: "rgba(75, 255, 255, 0.8)",
        },
      ],
    });

    setImpactTableData((currentTableData) => ({
      ...currentTableData,
      rows: labels.map((label, index) => {
        const cumulativePower = response.cumulative_power?.[index] ?? 0;
        const environmentalImpact = response.environmental_impacts?.[index] ?? 0;
        const currentTransportImpact = transportImpact[index] ?? 0;
        return {
          years: label,
          cumulativePower: Number(cumulativePower).toFixed(3),
          environmentalImpact: Number(environmentalImpact).toFixed(3),
          transportImpact: Number(currentTransportImpact).toFixed(3),
          totalEnvironmentalImpact: (
            cumulativePower * environmentalImpact +
            currentTransportImpact
          ).toFixed(3),
        };
      }),
    }));
  }

  async function fetchSiteImpact(siteId) {
    const response = await apiClient.get("environmentalvalues/", {
      query: { site_id: siteId },
    });
    setImpactResponse(response);
    updateImpactDisplays(response, distance);
    /*
    setImpactGraphData({
      labels: ["10", "15", "30"],
      datasets: [
        {
          label:
            "Environmental Impact per Functional Unit (mPt/kWh) (Peak power improves 1.4%/year, No reduction in EI production and recycle)",
          data: response.environmental_impacts,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          stack: "Stack 0",
        },
        {
          label:
            "Environmental Impact per Functional Unit (mPt/kWh) (Peak power improves 1.4%/year, EI production and recycle reduces by 10% every 5 years)",
          data: response.environmental_impacts_2,
          backgroundColor: "rgba(0, 255, 255, 0.8)",
          stack: "Stack 1",
        },
        {
          label:
            "Environmental Impact per Functional Unit (mPt/kWh) (Peak power improves 2.8%/year, No reduction in EI production and recycle)",
          data: response.environmental_impacts_3,
          backgroundColor: "rgba(255, 0, 255, 0.8)",
          stack: "Stack 2",
        },
        {
          label:
            "Environmental Impact per Functional Unit (mPt/kWh) (Peak power improves 2.8%/year, EI production and recycle reduces by 10% every 5 years)",
          data: response.environmental_impacts_2,
          backgroundColor: "rgba(255, 255, 0, 0.8)",
          stack: "Stack 3",
        },
      ],
    });
    */
  }

  const loadMapData = async (event) => {
    setIsMapClicked(true);
    const { latLng } = event;
    const clickedLat = Number(latLng.lat().toFixed(3));
    const clickedLng = Number(latLng.lng().toFixed(3));

    setClickedMarkers([
      <Marker position={{ lat: clickedLat, lng: clickedLng }} key="clickedCoord" />,
    ]);
    /*
    const currentSite = sites.filter((site) => site.id === selectedSite)[0];
    const coordinate = currentSite.coordinate;
    const [tempLat, tempLon] = coordinate.split(",");
    const siteLat = Number(tempLat);
    const siteLon = Number(tempLon);
    */

    const elevationResponse = await apiClient.get("getelevation", {
      auth: false,
      responseType: "text",
      headers: { Accept: "text/plain" },
      query: { lat: clickedLat, lon: clickedLng },
    });
    const elevation = Number(elevationResponse);
    if (elevation < -500) {
      return;
    }
    const extentResponse = await apiClient.get("extent", {
      auth: false,
      responseType: "text",
      headers: { Accept: "text/plain" },
      query: { lat: clickedLat, lon: clickedLng },
    });
    const extents = extentResponse
      .split("\n")
      .filter((e) => e !== "")
      .map((e) => Number(e));
    const useableDatabases = databases.filter((e, i) => extents[i] === 1);
    setLat(clickedLat);
    setLng(clickedLng);
    setDatabaseDisabled(false);
    setDatabaseOptions(useableDatabases);
    setDatabase(useableDatabases[0]);
  };

  const mapClicked = async (event) => {
    handleLoadingOpen();
    try {
      await loadMapData(event);
    } finally {
      handleLoadingClose();
    }
  };

  const loadOnGridCalculation = async () => {
    if (!isMapClicked) {
      setShowMapNotClickedAlert(true);
      return;
    }
    let optimizeParameters = {};

    if (optimize.code === "n") {
      optimizeParameters = { angle: slope, aspect: azimuth };
    } else if (optimize.code === "s") {
      optimizeParameters = { optimalinclination: 1, aspect: azimuth };
    } else if (optimize.code === "sa") {
      optimizeParameters = { optimalangles: 1 };
    }

    const pvcalcResponse = await apiClient.get("pv_calc", {
      auth: false,
      responseType: "text",
      headers: { Accept: "text/plain" },
      query: {
        outputformat: "basic",
        lat,
        lon: lng,
        raddatabase: database,
        peakpower: peakPVPower,
        loss: systemLoss,
        mountingplace: mountingPosition.code,
        pvtechchoice: pvTechnology.code,
        ...optimizeParameters,
        usehorizon: 1,
        userhorizon: "",
      },
    });
    const pvcalcLines = pvcalcResponse.split("\r\n");
    const monthlyData = pvcalcLines.slice(2, 14).map((i) => i.split("\t").slice(1, 4));
    const monthlyPvoutput = monthlyData.map((i) => Number(i[0]));
    const monthlyRadiation = monthlyData.map((i) => Number(i[1]));
    setMonthlyGraphData({
      labels: localizedMonths,
      units: { x: t("month"), y: t("on_grid_chart_unit") },
      datasets: [
        {
          label: currentTranslation.monthly_performance_graph_pv_output,
          unit: "kWh",
          data: monthlyPvoutput,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        },
        {
          label: currentTranslation.monthly_performance_graph_radiation,
          unit: "kWh/m²",
          data: monthlyRadiation,
          backgroundColor: "rgba(0, 255, 255, 0.8)",
        },
      ],
    });
    setSlopeAngle(Number(pvcalcLines[0]));
    setAzimuthAngle(Number(pvcalcLines[1]));
    const yearlyData = pvcalcLines[14].split("\t");
    setYearlyPVEnergyProduction(Number(yearlyData[1]));
    setYearlyInplaneIrradiation(Number(yearlyData[2]));
    setYearToYearVariability(Number(yearlyData[3]));
    setAngleOfIncidence(Number(pvcalcLines[15].split("\t")[1]));
    setSpectralEffects(Number(pvcalcLines[16].split("\t")[1]) || pvcalcLines[16].split("\t")[1]);
    setTemperatureAndLowIrradiance(Number(pvcalcLines[17].split("\t")[1]));
    setTotalLoss(Number(pvcalcLines[18].split("\t")[1]));

    if (pvprice) {
      if (Number.isNaN(systemCost) || Number.isNaN(interest) || Number.isNaN(serviceLife)) {
        setPVElectricityCost("");
      } else {
        setPVElectricityCost(
          (annuity(serviceLife, interest, systemCost) + 0.02 * systemCost).toFixed(3)
        );
      }
    }
  };

  const caculateOnGrid = async () => {
    handleLoadingOpen();
    try {
      await loadOnGridCalculation();
    } finally {
      handleLoadingClose();
    }
  };

  const loadOffGridCalculation = async () => {
    const shscalcResponse = await apiClient.get("shs_calc", {
      auth: false,
      responseType: "text",
      headers: { Accept: "text/plain" },
      query: {
        outputformat: "basic",
        lat,
        lon: lng,
        raddatabase: database,
        peakpower: offGridPeakPVPower,
        batterysize: offGridBatteryCapacity,
        cutoff: offGridDischargeCutoffLimit,
        consumptionday: offGridConsumptionPerDay,
        angle: offGridSlope,
        aspect: offGridAzimuth,
        usehorizon: 1,
        userhorizon: "",
        hourconsumption: "",
      },
    });
    const shscalcLines = shscalcResponse.split("\r\n");
    const monthlyData = shscalcLines.slice(5, 17).map((i) => i.split("\t").slice(1, 5));
    const batteryState = shscalcLines.slice(17, 28).map((i) => i.split("\t"));
    const monthlyEnergyOutput = monthlyData.map((i) => i[0]);
    const mothlyEnergyNotCaptured = monthlyData.map((i) => i[1]);
    const monthlyDaysFull = monthlyData.map((i) => i[2]);
    const monthlyDaysEmpty = monthlyData.map((i) => i[3]);
    setMonthlyGraphData({
      labels: localizedMonths,
      units: { x: t("month"), y: t("off_grid_chart_unit") },
      datasets: [
        {
          label: currentTranslation.monthly_performance_graph_energy_output,
          unit: "kWh",
          data: monthlyEnergyOutput,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        },
        {
          label: currentTranslation.monthly_performance_graph_energy_not_captured,
          unit: "kWh",
          data: mothlyEnergyNotCaptured,
          backgroundColor: "rgba(0, 255, 255, 0.8)",
        },
        {
          label: currentTranslation.monthly_performance_graph_days_with_battery_full,
          unit: currentTranslation.days,
          data: monthlyDaysFull,
          backgroundColor: "rgba(255, 0, 255, 0.8)",
        },
        {
          label: currentTranslation.monthly_performance_graph_days_with_battery_empty,
          unit: currentTranslation.days,
          data: monthlyDaysEmpty,
          backgroundColor: "rgba(255, 255, 0, 0.8)",
        },
      ],
    });
    const percentileGraphLabel = batteryState.map(
      (i) => `${Number(i[0]).toFixed(0)}-${Number(i[1]).toFixed(0)}`
    );
    const percentileData = batteryState.map((i) => i[2]);
    setPercentileGraphData({
      labels: percentileGraphLabel,
      units: { x: t("battery_charge_state"), y: "%" },
      datasets: {
        label: currentTranslation.percentile_graph_charge_state_frequency,
        data: percentileData,
        backgroundColor: "rgba(255, 255, 255, 0.8)",
      },
    });
    setOffGridFullBatteryDays(shscalcLines[1]);
    setOffGridEmptyBatteryDays(shscalcLines[3]);
    setOffGirdAverageEnergyNotCaptured(shscalcLines[2]);
    setOffGridAverageEnergyMissing(shscalcLines[4]);
  };

  const caculateOffGrid = async () => {
    handleLoadingOpen();
    try {
      await loadOffGridCalculation();
    } finally {
      handleLoadingClose();
    }
  };

  async function fetchPanelResult(siteId, siteList = sites) {
    const response = await apiClient.get(`panel_results/${encodeURIComponent(siteId)}/`);
    setServiceLife(parseInt(response.service_life_rates.length));
    const currentSite =
      (location.state && location.state.prop && location.state.prop.coordinate
        ? location.state.prop
        : null) || siteList.find((site) => String(site.id) === String(siteId));
    if (!currentSite || !currentSite.coordinate) {
      return;
    }
    const coordinate = currentSite.coordinate;
    const [tempLat, tempLon] = coordinate.split(",");
    const siteLat = Number(tempLat);
    const siteLon = Number(tempLon);
    setSiteLat(siteLat);
    setSiteLng(siteLon);
    setSiteMarkers([
      <Marker
        position={{ lat: siteLat, lng: siteLon }}
        key="siteCoord"
        icon={"https://maps.google.com/mapfiles/ms/icons/yellow-dot.png"}
      />,
    ]);
    setCenter({
      lat: siteLat,
      lng: siteLon,
    });
    setSlope(currentSite.tilt);
    setAzimuth(currentSite.azimuth);
    setPeakPVPower(currentSite.panel_output);
    setSystemLoss(Math.ceil(Math.abs(response.exceedance_level) * 100) + 14);
  }

  async function fetchSites() {
    if (navigationSite) {
      handleLoadingOpen();
    }

    try {
      const response = await apiClient.get("sites");
      setSites(response);
      if (navigationSite) {
        const siteId = navigationSite.id ?? navigationSite.site;
        setSelectedSite(siteId);
        await Promise.all([fetchPanelResult(siteId, response), fetchSiteImpact(siteId)]);
      }
    } finally {
      if (navigationSite) {
        handleLoadingClose();
      }
    }
  }

  // async function
  async function doSiteChanged(event) {
    handleLoadingOpen();
    try {
      await fetchPanelResult(event.target.value);
      await fetchSiteImpact(event.target.value);
    } finally {
      handleLoadingClose();
    }
  }

  function selectChange(event) {
    setIsMapClicked(false);
    setSelectedSite(event.target.value);
    doSiteChanged(event);
  }

  useEffect(() => {
    setPVTechnologyOptions((options) => {
      const labels = {
        crystSi: t("pv_technology_dropdown_crystalline_silicon"),
        CIS: t("pv_technology_dropdown_cis"),
        CdTe: t("pv_technology_dropdown_cdte"),
        Unknown: t("pv_technology_dropdown_unknown"),
      };
      return options.map((option) => ({ ...option, label: labels[option.code] || option.label }));
    });
    setImpactTableData((current) => ({
      ...current,
      columns: [
        { Header: t("environmentalUi.years", { ns: "pages" }), accessor: "years" },
        {
          Header: t("environmentalUi.cumulativePower", { ns: "pages" }),
          accessor: "cumulativePower",
        },
        {
          Header: t("environmentalUi.impactPerUnit", { ns: "pages" }),
          accessor: "environmentalImpact",
        },
        {
          Header: t("environmentalUi.transportImpact", { ns: "pages" }),
          accessor: "transportImpact",
        },
        {
          Header: t("environmentalUi.totalImpact", { ns: "pages" }),
          accessor: "totalEnvironmentalImpact",
        },
      ],
    }));
    setMonthlyGraphData((current) => ({
      ...current,
      labels: localizedMonths,
      units: { ...current.units, x: t("month") },
    }));
    setPercentileGraphData((current) => ({
      ...current,
      units: { ...current.units, x: t("battery_charge_state") },
    }));
    setMountingPositionOptions((options) =>
      options.map((option) => {
        if (option.code === "free") {
          return { ...option, label: t("mounting_position_dropdown_free_standing") };
        }
        return {
          ...option,
          label: t("mounting_position_dropdown_roof_added_building_integrated"),
        };
      })
    );
    setOptimizeOptions((options) =>
      options.map((option) => {
        const labels = {
          n: t("optimize_dropdown_none"),
          s: t("optimize_dropdown_slope"),
          sa: t("optimize_dropdown_slope_and_azimuth"),
        };
        return { ...option, label: labels[option.code] || option.label };
      })
    );
  }, [i18n.resolvedLanguage]);
  useEffect(() => {
    setPVTechnology(
      (current) =>
        pvTechnologyOptions.find((option) => option.code === current?.code) ||
        pvTechnologyOptions[0]
    );
  }, [pvTechnologyOptions]);

  useEffect(() => {
    setMountingPosition(
      (current) =>
        mountingPositionOptions.find((option) => option.code === current?.code) ||
        mountingPositionOptions[0]
    );
  }, [mountingPositionOptions]);

  useEffect(() => {
    setOptimize(
      (current) =>
        optimizeOptions.find((option) => option.code === current?.code) || optimizeOptions[0]
    );
  }, [optimizeOptions]);

  useEffect(() => {
    fetchSites();
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    if (impactResponse.service_life_years) {
      updateImpactDisplays(impactResponse, distance);
    }
  }, [distance]);

  const valueCompare = (option, newValue) => option.code === newValue.code;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loadingOpen}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MDBox pb={3}>
        <MDBox mt={8} ml={5} mr={5}>
          <Card sx={{ height: "100%" }}>
            <MDBox mx={2} mt={-3}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, index) => (
                  <Step key={index}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </MDBox>
            <MDBox p={3}>
              <MDBox>
                {getStepContent(activeStep, currentTranslation)}
                <MDBox mt={2} width="100%" display="flex" justifyContent="space-between">
                  {activeStep === 0 ? (
                    <MDBox />
                  ) : (
                    <MDButton variant="gradient" color="light" onClick={handleBack}>
                      {t("back", { ns: "common" })}
                    </MDButton>
                  )}
                  {isLastStep ? (
                    <MDBox />
                  ) : (
                    <MDButton type="submit" variant="gradient" color="dark" onClick={handleForward}>
                      {t("next", { ns: "common" })}
                    </MDButton>
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
        <MDBox p={5}>
          <FormControl fullWidth>
            <InputLabel id="sites-label">{t("forecastUi.sites", { ns: "pages" })}</InputLabel>
            <Select
              labelId="sites-label"
              id="sites-select"
              value={selectedSite}
              label={t("forecastUi.selectSite", { ns: "pages" })}
              sx={{ minHeight: "50px", maxWidth: "200px" }}
              onChange={selectChange}
            >
              {sites.map((site, index) => (
                <MenuItem key={index} value={site.id}>
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
        <MDBox py={3}>
          <MDBox mt={8}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={3}>
                <Card sx={{ position: "relative", mt: -8, mx: 3, py: 2, px: 4 }}>
                  <Tabs orientation="horizontal" value={tabValue} onChange={handleSetTabValue}>
                    <Tab label={currentTranslation.grid_connected} />
                    <Tab label={currentTranslation.off_grid} />
                  </Tabs>
                  <TabPanel value={tabValue} index={0}>
                    <SectionHeader
                      variant="compact"
                      align="center"
                      title={currentTranslation.input}
                    />
                    <ForecastAutocompleteField
                      mt={3}
                      helpText={tHelp("forecast.solarRadiationDatabase")}
                    >
                      <Autocomplete
                        disabled={databaseDisabled}
                        value={database}
                        onChange={(e, v) => setDatabase(v)}
                        inputValue={inputDatabase}
                        onInputChange={(e, v) => setInputDatabase(v)}
                        options={databaseOptions}
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            variant="standard"
                            label={currentTranslation.solar_radiation_database}
                          />
                        )}
                      />
                    </ForecastAutocompleteField>
                    <ForecastAutocompleteField helpText={tHelp("forecast.pvTechnology")}>
                      <Autocomplete
                        value={pvTechnology}
                        onChange={(e, v) => setPVTechnology(v)}
                        inputValue={inputPVTechnology}
                        onInputChange={(e, v) => setInputPVTechnology(v)}
                        options={pvTechnologyOptions}
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            variant="standard"
                            label={currentTranslation.pv_technology}
                          />
                        )}
                        isOptionEqualToValue={valueCompare}
                      />
                    </ForecastAutocompleteField>
                    <FormField
                      type="number"
                      label={currentTranslation.installed_peak_pv_power_kwp}
                      value={peakPVPower}
                      onChange={(e) => setPeakPVPower(e.target.value)}
                      helpText={tHelp("forecast.installedPeakPower")}
                    />
                    <FormField
                      type="number"
                      label={currentTranslation.system_loss}
                      value={systemLoss}
                      onChange={(e) => setSystemLoss(e.target.value)}
                      helpText={tHelp("forecast.systemLoss")}
                    />
                    <Divider />
                    <MDBox mb={1.625} display="inline-block">
                      <MDTypography
                        component="label"
                        variant="button"
                        fontWeight="bold"
                        color="text"
                        textTransform="capitalize"
                      >
                        {currentTranslation.fixed_mounting_options}
                      </MDTypography>
                    </MDBox>
                    <ForecastAutocompleteField helpText={tHelp("forecast.mountingPosition")}>
                      <Autocomplete
                        value={mountingPosition}
                        onChange={(e, v) => setMountingPosition(v)}
                        inputValue={intputMountingPosition}
                        onInputChange={(e, v) => setInputMountingPosition(v)}
                        options={mountingPositionOptions}
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            variant="standard"
                            label={currentTranslation.mounting_position}
                          />
                        )}
                        isOptionEqualToValue={valueCompare}
                      />
                    </ForecastAutocompleteField>
                    <FormField
                      type="number"
                      label={currentTranslation.slope}
                      value={slope}
                      onChange={(e) => setSlope(e.target.value)}
                      helpText={tHelp("forecast.slope")}
                    />
                    <FormField
                      type="number"
                      label={currentTranslation.azimuth}
                      value={azimuth}
                      onChange={(e) => setAzimuth(e.target.value)}
                      helpText={tHelp("forecast.azimuth")}
                    />
                    <MDBox mb={3}>
                      <Autocomplete
                        value={optimize}
                        onChange={(e, v) => setOptimize(v)}
                        inputValue={inputOptimize}
                        onInputChange={(e, v) => setInputOptimize(v)}
                        options={optimizeOptions}
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            variant="standard"
                            label={currentTranslation.optimize}
                          />
                        )}
                        isOptionEqualToValue={valueCompare}
                      />
                    </MDBox>
                    <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
                      <MDBox mt={0.5}>
                        <Switch
                          checked={pvprice}
                          onChange={() => {
                            setPvprice(!pvprice);
                            setSystemCostDisabled(pvprice);
                            setInterestDisabled(pvprice);
                            setServiceLifeDisabled(pvprice);
                          }}
                        />
                      </MDBox>
                      <MDBox width="80%" ml={0.5}>
                        <MDTypography variant="button" fontWeight="regular" color="text">
                          {currentTranslation.pv_electricity_price}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    <FormField
                      type="number"
                      label={currentTranslation.pv_system_cose}
                      defaultValue={systemCost}
                      disabled={systemCostDisabled}
                      onChange={(e) => setSystemCost(Number(e.target.value))}
                      helpText={tHelp("forecast.pvSystemCost")}
                    />
                    <FormField
                      type="number"
                      label={currentTranslation.interest}
                      defaultValue={interest}
                      disabled={interestDisabled}
                      onChange={(e) => setInterest(Number(e.target.value))}
                    />
                    <FormField
                      type="number"
                      label={currentTranslation.serviceLife}
                      value={serviceLife}
                      disabled={serviceLifeDisabled}
                      onChange={(e) => setServiceLife(Number(e.target.value))}
                      helpText={tHelp("forecast.serviceLife")}
                    />
                    <MDButton variant="gradient" color="dark" onClick={caculateOnGrid}>
                      {currentTranslation.visualize_result}
                    </MDButton>
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <SectionHeader
                      variant="compact"
                      align="center"
                      title={currentTranslation.input}
                    />
                    <ForecastAutocompleteField
                      mt={3}
                      helpText={tHelp("forecast.solarRadiationDatabase")}
                    >
                      <Autocomplete
                        disabled={databaseDisabled}
                        value={database}
                        onChange={(e, v) => setDatabase(v)}
                        inputValue={inputDatabase}
                        onInputChange={(e, v) => setInputDatabase(v)}
                        options={databaseOptions}
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            variant="standard"
                            label={currentTranslation.solar_radiation_database}
                          />
                        )}
                      />
                    </ForecastAutocompleteField>
                    <FormField
                      type="number"
                      label={currentTranslation.installed_peak_pv_power_wp}
                      defaultValue={offGridPeakPVPower}
                      onChange={(e) => setOffGridPeakPVPower(e.target.value)}
                      helpText={tHelp("forecast.installedPeakPower")}
                    />
                    <FormField
                      type="number"
                      label={currentTranslation.battery_capacity}
                      defaultValue={offGridBatteryCapacity}
                      onChange={(e) => setOffGridBatteryCapacity(e.target.value)}
                      helpText={tHelp("forecast.batteryCapacity")}
                    />
                    <FormField
                      type="number"
                      label={currentTranslation.discharge_cutoff_limit}
                      defaultValue={offGridDischargeCutoffLimit}
                      onChange={(e) => setOffGridDischargeCutoffLimit(e.target.value)}
                      helpText={tHelp("forecast.dischargeCutoffLimit")}
                    />
                    <FormField
                      type="number"
                      label={currentTranslation.consumption_per_day}
                      defaultValue={offGridConsumptionPerDay}
                      onChange={(e) => setOffGridConsumptionPerDay(e.target.value)}
                      helpText={tHelp("forecast.consumptionPerDay")}
                    />
                    <FormField
                      type="number"
                      label={currentTranslation.slope}
                      defaultValue={offGridSlope}
                      onChange={(e) => setOffGridSlop(e.target.value)}
                      helpText={tHelp("forecast.slope")}
                    />
                    <FormField
                      type="number"
                      label={currentTranslation.azimuth}
                      defaultValue={offGridAzimuth}
                      onChange={(e) => setOffGridAzimuth(e.target.value)}
                      helpText={tHelp("forecast.azimuth")}
                    />
                    <MDButton variant="gradient" color="dark" onClick={caculateOffGrid}>
                      {currentTranslation.visualize_result}
                    </MDButton>
                  </TabPanel>
                </Card>
              </Grid>
              <Grid item xs={12} md={6} lg={9}>
                <MDBox mb={3} mt={-7.8}>
                  <GeoMap
                    center={center}
                    siteMarkers={siteMarkers}
                    clickedMarkers={clickedMarkers}
                    onClick={mapClicked}
                    lat={lat}
                    lng={lng}
                    siteLat={siteLat}
                    siteLng={siteLng}
                    setDistance={setDistance}
                  />
                </MDBox>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6} lg={6}>
                    <MDBox mt={10}>
                      {tabValue === 0 ? (
                        <Card sx={{ position: "relative", mt: -8, py: 2, px: 4 }}>
                          <SectionHeader
                            variant="compact"
                            align="center"
                            title={currentTranslation.simulation_outputs}
                          />
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.slope_angle}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {slopeAngle}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.azimuth_angle}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {azimuthAngle}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.yearly_pv_energy_production}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {yearlyPVEnergyProduction}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.yearly_in_plane_irradiation}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {yearlyInplaneIrradiation}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.year_to_year_variability}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {yearToYearVariability}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.changes_in_output_due_to}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {" "}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              &nbsp;&nbsp;{currentTranslation.angle_of_incidence}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {angleOfIncidence}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              &nbsp;&nbsp;{currentTranslation.spectral_effects}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {spectralEffects}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              &nbsp;&nbsp;{currentTranslation.temperature_and_low_irradiance}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {temperatureAndLowIrradiance}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.total_loss}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {totalLoss}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.pv_electricity_cost}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {pvElectricityCost}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        </Card>
                      ) : (
                        <Card sx={{ position: "relative", mt: -8, py: 2, px: 4 }}>
                          <SectionHeader
                            variant="compact"
                            align="center"
                            title={currentTranslation.simulation_outputs}
                          />
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.percentage_days_with_full_battery}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {offGridFullBatteryDays}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.percentage_days_with_empty_battery}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {offGridEmptyBatteryDays}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.average_energy_not_captured}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {offGridAverageEnergyNotCaptured}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                              {currentTranslation.average_energy_missing}:
                            </MDTypography>
                            <MDBox ml={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                {offGridAverageEnergyMissing}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        </Card>
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <MDBox mt={2}>
                      <ReportsBarNoLabelChart
                        color="info"
                        date=""
                        title={currentTranslation.monthly_performance}
                        chart={monthlyGraphData}
                      />
                    </MDBox>
                    {tabValue === 1 ? (
                      <MDBox mt={6}>
                        <ReportsBarChart
                          color="success"
                          date=""
                          title={currentTranslation.percentile}
                          chart={percentileGraphData}
                        />
                      </MDBox>
                    ) : null}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <MDBox px={3}>
              <Collapse in={showMapNotClickedAlert}>
                <Alert
                  severity="error"
                  action={
                    <IconButton
                      aria-label="close"
                      color="error"
                      size="small"
                      onClick={() => setShowMapNotClickedAlert(false)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ mb: 2 }}
                >
                  {t("forecastUi.mapDestination", { ns: "pages" })}
                </Alert>
              </Collapse>
            </MDBox>
            <MDBox mt={5}>
              <br />
              <Grid container spacing={3}>
                <Grid item xs={12} md={12} lg={12}>
                  <Card sx={{ width: "100%" }}>
                    <MDBox display="flex">
                      <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                        {t("environmentalUi.totalImpactPerUnit", { ns: "pages" })}
                      </MDTypography>
                    </MDBox>
                    <MDBox p={2}>
                      <DataTable
                        table={impactTableData}
                        entriesPerPage={false}
                        showTotalEntries={false}
                      />
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} md={12} lg={12}>
                  <MDBox mb={1.5} mt={1.5}>
                    <MultiAxisChart
                      icon={{ component: "show_chart" }}
                      title={t("environmentalValue.panelServiceLifeAndOutput", { ns: "pages" })}
                      description="&nbsp;"
                      chart={impactAndCumulativePowerGraphData}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </MDBox>
          {/* }
          <MDBox mt={1.5}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5}>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5}>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5}>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <MDBox mb={1.5}>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox mt={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mt={3}>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mt={3}>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mt={3}>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
          { */}
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default PowerGenerationForecast;
